import { useContext, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import CourseCardDashboard from "../components/CourseCardDashboard";
import { NameContext } from "../context/NameContext";
import UpdateUserProfileModal from "../components/UpdateUserProfileModal";
import PasswordChangeModal from "./PasswordChangeModal";
import { PaperGradientBG } from "../kits";
import ZenEditorDemo from "./ZenEditorDemo";
import LMSDemo from "./LMSDemo";
import BookingModal from "./BookingModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import ContactForm from "./ContactForm";

const Dashboard = () => {
  const { userId, logout, token, mustChangePassword, setMustChangePassword } = useAuth();
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const { setName, setIsNameEntered } = useContext(NameContext);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get("demo");
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    if (showBooking) {
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const targetScroll = Math.max(0, scrollTop - (viewportHeight / 4));
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }, [showBooking]);

  useEffect(() => {
    if (demoMode) return;
    if (!userId) {
      navigate("/login");
      return;
    }

    const fetchCourses = async () => {
      try {
        const res = await fetch(
          `https://www.denisbitter.de/stage01/api/get_user_courses.php?userId=${userId}`
        );
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          const rawCourses = data.data;
          const normalizedCourses = rawCourses.map((c) => ({
            id: c.course_id,
            slug: c.course_slug,
            title: c.course_title,
            description: c.course_description,
            iconName: c.course_icon,
            category: c.course_category,
            startDate: c.course_startDate,
            customDate: c.registration_custom_date,
            pdfPath: c.registration_pdf_path,
            firstName: c.registrant_first_name,
            lastName: c.registrant_last_name,
            registrationDate: c.registration_date,
            registrationId: c.registration_id,
          }));

          setCourses(normalizedCourses);
          const name = normalizedCourses[0]?.firstName || "";
          if (name) {
            setName(name);
            setIsNameEntered(true);
          }
          setFirstName(name);
        } else {
          setError(data.message || "Keine Kurse gefunden.");
        }
      } catch (err) {
        console.error("Fehler beim Laden:", err);
        setError("Fehler beim Laden der Kurse.");
      }
    };

    fetchCourses();
  }, [userId, setName, setIsNameEntered, navigate, demoMode]);

  if (demoMode === "zeneditor") return <ZenEditorDemo />;
  if (demoMode === "lms") return <LMSDemo />;

  const displayName = firstName || `user_${userId}`;

  return (
    <PaperGradientBG variant="zenBlack">
      <>
        {mustChangePassword && (
          <PasswordChangeModal onClose={() => setMustChangePassword(false)} />
        )}
        <div className="min-h-screen">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 font-mono">

            {/* Header Terminal */}
            <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40 mb-8">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-xs text-[#AC8E66] select-none">dashboard</span>
              </div>
              <div className="px-5 sm:px-6 py-5 bg-[#151515]">
                <p className="text-sm text-[#EFEBDC]/50 mb-1">
                  <span className="text-[#AC8E66]">$</span> moin, {displayName}
                </p>
                <p className="text-xs text-[#EFEBDC]/40 ml-3 mb-3">
                  <span className="text-[#AC8E66]/80">&gt;</span> deine trainings ({courses.length})
                </p>
                <div className="flex items-center gap-4">
                  <UpdateUserProfileModal initialData={courses[0]} />
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-xs text-red-400/80 mb-4 font-mono">
                <span className="text-red-400/60">&gt;</span> error: {error}
              </p>
            )}

            {/* Course Cards */}
            <div className="space-y-4 mb-12">
              {courses.length > 0 ? (
                courses.map((course) => (
                  <CourseCardDashboard key={course.id} course={course} />
                ))
              ) : (
                !error && (
                  <p className="text-xs text-[#EFEBDC]/40 font-mono">
                    <span className="text-[#AC8E66]/80">&gt;</span> keine kurse gefunden.
                  </p>
                )
              )}
            </div>

            {/* Demo Section */}
            <div className="border-t border-[#AC8E66]/10 pt-8">
              <p className="text-sm text-[#EFEBDC]/50 mb-6 font-mono">
                <span className="text-[#AC8E66]">$</span> ls demos/
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ZenEditor */}
                <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
                    <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                    <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                    <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                    <span className="ml-2 text-[10px] text-[#AC8E66] select-none">zeneditor</span>
                  </div>
                  <div className="px-4 py-4 bg-[#151515]">
                    <p className="text-sm text-[#EFEBDC]/80 font-mono mb-1">ZenEditor</p>
                    <p className="text-[10px] text-[#EFEBDC]/30 mb-3">Hybrid Content Editor</p>
                    <p className="text-xs text-[#EFEBDC]/50 mb-3 leading-relaxed">
                      Kombiniere EditorJS mit Markdown-Support. Hybrid, flexibel und portabel.
                    </p>
                    <div className="space-y-1 mb-4">
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> Markdown & EditorJS</p>
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> Live Preview</p>
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> Export & Import</p>
                    </div>
                    <button
                      onClick={() => navigate("/dashboard?demo=zeneditor")}
                      className="w-full py-2 text-xs font-mono text-[#AC8E66] bg-[#1c1c1c] border border-[#AC8E66]/30 rounded hover:border-[#AC8E66]/60 hover:bg-[#1e1e1e] transition-all cursor-pointer"
                    >
                      [ demo starten → ]
                    </button>
                  </div>
                </div>

                {/* LMS */}
                <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
                    <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                    <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                    <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                    <span className="ml-2 text-[10px] text-[#AC8E66] select-none">lms</span>
                  </div>
                  <div className="px-4 py-4 bg-[#151515]">
                    <p className="text-sm text-[#EFEBDC]/80 font-mono mb-1">LMS System</p>
                    <p className="text-[10px] text-[#EFEBDC]/30 mb-3">Learning Management</p>
                    <p className="text-xs text-[#EFEBDC]/50 mb-3 leading-relaxed">
                      Kurs-Editor, User-Management, Progress-Tracking und Zertifikate.
                    </p>
                    <div className="space-y-1 mb-4">
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> Kurs-Management</p>
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> Progress-Tracking</p>
                      <p className="text-xs text-[#EFEBDC]/40"><span className="text-green-400/60">✓</span> User-Dashboard</p>
                    </div>
                    <button
                      onClick={() => navigate("/dashboard?demo=lms")}
                      className="w-full py-2 text-xs font-mono text-[#AC8E66] bg-[#1c1c1c] border border-[#AC8E66]/30 rounded hover:border-[#AC8E66]/60 hover:bg-[#1e1e1e] transition-all cursor-pointer"
                    >
                      [ demo starten → ]
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-[#EFEBDC]/30 mt-4 text-center font-mono">
                interesse?{" "}
                <button
                  onClick={() => setShowBooking(true)}
                  className="text-[#AC8E66]/70 hover:text-[#AC8E66] underline underline-offset-2 cursor-pointer transition-colors"
                >
                  kontakt aufnehmen →
                </button>
              </p>

              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>

        {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
      </>
    </PaperGradientBG>
  );
};

export default Dashboard;
